import React, { PureComponent, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { fieldHelper } from '@builttocreate/engine-utils';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from './FieldHeader';
import IconCheckbox from '../../misc/IconCheckbox';
import Tooltip from '../../misc/Tooltip';

import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';

const { handleMultiSelectOptionToggle, getMultiSelectFieldValue } = fieldHelper;

const StyledWrapper = styled.div`
  height: ${props => props.measure ? 'auto' : '100%'};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledVerticalOptionsWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.borderRadius}px;
  border: ${(props) => props.border};
  overflow-y: ${(props) => (props.disabled ? 'hidden' : 'auto')};
`;

const StyledHorizontalOptionsWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledVerticalOption = styled.div`
  display: flex; 
  flex-direction: row;
  align-items: center;
  background-color: ${(props) => props.backgroundColor};
  padding: ${(props) => props.padding}px;
  border-bottom: ${(props) => props.border};
  flex-grow: 1;
`;

const StyledHorizontalOption = styled.div`
  display: flex; 
  flex-grow: 1;
  flex-direction: row;
  align-items: center;
  background-color: ${(props) => props.backgroundColor};
  padding: ${(props) => props.padding}px;
  border-radius: ${(props) => props.borderRadius}px;
  border: ${(props) => props.border};
  margin-right: 4px;
  margin-bottom: 4px;
  ${(props) => {
    return props.width !== undefined && props.width !== 0 ? `max-width: ${props.width}px; min-width: ${props.width}px;` : '';
  }};
`;

const StyledLabel = styled.label`
  width:100%;
  background-color: ${(props) => props.backgroundColor};
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'white-space: nowrap;' : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'overflow: hidden;' : '')}
  margin-left: 8px;
`;

const StyledTooltipWrapper = styled.div`
  position:absolute;
  right:0;
  top:0px;
`;

class MultiSelectField extends PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    value: PropTypes.array,
    options: PropTypes.array,
    disabled: PropTypes.bool,
    multi: PropTypes.bool,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,

  }

  constructor(props) {

    super(props);

    this.state = {
      focused: false
    };

  }

  componentDidUpdate(prevProps) {

    const { wrapperRef } = this.state;
    const { active, measure, onMeasureCompleted, measuredHeight, measuredOn, updatedOn, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (active !== prevProps.active) {
      this.setState({ focused: active });
    }

    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && measure && onMeasureCompleted && refHeightAvailable && (!measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn))) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, { height: refHeightAvailable, measuredOn: new Date().getTime() });
      });
    }

  }

  render() {

    const { wrapperRef } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      options,
      multi,
      displayType,
      
      //Used for measuring
      measure,
      onMeasureCompleted,
      measuredHeight,
      measuredOn,
      updatedOn,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
      tabIndex,

      active,
      decorators,
      onDecoratorClick,
      fieldMode,

    } = this.props;

    const validOptions = options && options.length > 0 ? options.filter((option) => !option.deleted) : [];

    const selectedOptions = getMultiSelectFieldValue(value);

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    const optionNodes = validOptions.map((option, i) => {

      const lastIndex = (validOptions.length - 1) === i;
      const selected = selectedOptions.indexOf(option._id) !== -1;

      const OptionNode = displayType === FieldDisplayTypes.horizontal ? StyledHorizontalOption : StyledVerticalOption;

      return (
        <OptionNode
          key={option._id}
          data-testid={`${fieldPositionId}-multi-select-field-option-${option._id}`}
          {...{
            border: lastIndex && displayType !== FieldDisplayTypes.horizontal ? '' : border,
            borderRadius,
            width: option.width,
            padding,
            backgroundColor,
          }}
          tabIndex={tabIndex}
        >
          <IconCheckbox
            type={multi ? 'checkbox' : 'radio'}
            id={`${fieldPositionId}-multi-select-field-option-${option._id}`}
            selected={selected}
            disabled={disabled}
            onChange={(value) => this.handleChange(option._id, value)}
          />
          <StyledLabel
            htmlFor={option._id}
            data-testid={`${fieldPositionId}-multi-select-field-option-${option._id}-label`}
            {...{
              fontSize,
              fontColor,
              fontStyle,
              fontWeight,
              textAlign,
              textTransform,
              textDecoration,
              textOverflow
            }}
          >
            {option.value}
          </StyledLabel>
        </OptionNode>
      );
    });

    const OptionsWrapperNode = displayType === FieldDisplayTypes.horizontal ? StyledHorizontalOptionsWrapper : StyledVerticalOptionsWrapper;

    return (
      <StyledWrapper
        {...{ margin, measure }}
        data-testid={`${fieldPositionId}-multi-select-field`}
        ref={(e) => {
          if (e && !wrapperRef) this.setState({ wrapperRef: e });
        }}
        onBlur={() => {
          /**
          * Logic below sets the focused state to false when user blurs the field.
          *
          * This onBlur event will be triggered when:
          * 1. User blurs the field by clicking on some other field (ie active: false)
          * 2. User blurs the checkbox of the current option that he has placed his cursor
          *    on by clicking on the option text while the field is still focused on (ie active :true)
          */
          if (!active) this.setState({ focused: false });

        }}
      >
        <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
            }}
          />
          <FieldDecorators
            decorators={decorators}
            fieldMode={fieldMode}
            fieldId={fieldId}
            fieldIdentifier={fieldIdentifier}
            fieldPositionId={fieldPositionId}
            onDecoratorClick={onDecoratorClick}
          />
        </StyledFieldHeader>
        {getTooltip()}
        <OptionsWrapperNode
          data-testid={`${fieldPositionId}-multi-select-field-options-wrapper`}
          {...{
            disabled,
            border,
            borderRadius,
          }}
        >
          {optionNodes}
        </OptionsWrapperNode>
      </StyledWrapper>
    );
  }

  handleChange = (optionId) => {

    const { fieldId, fieldIdentifier, fieldPositionId, multi, value, onChange, disabled, onFocus } = this.props;
    const { focused } = this.state;

    const selectedOptions = getMultiSelectFieldValue(value);

    const nextSelections = handleMultiSelectOptionToggle(multi, selectedOptions, optionId);

    /**
     * The multiSelect field should only fire onFocus for the first check that is pressed.
     * For any additional checkbox click it should only use onChange
     */
    if (!disabled && onFocus && !focused) {

      this.setState({ focused: true });

      onFocus(fieldId, fieldIdentifier, fieldPositionId, {});

    }

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextSelections);

  }

}

export default memo(MultiSelectField);
